import {computePosition, flip, shift, offset} from '@floating-ui/dom';


class Toggletip {
  #toggle;
  #tooltip;
  #tooltipMessage;
  #clickedOpen;

  constructor(toggle) {
    this.#toggle = toggle;
    this.#tooltip = document.querySelector(`#${toggle.getAttribute('aria-describedby')}`);
    this.#tooltipMessage = this.#toggle.getAttribute('data-toggletip-content');
    this.#clickedOpen = false;

    // Add expanded ARIA
    this.#toggle.setAttribute('aria-expanded', 'false');

    this.hideTooltip = this.hideTooltip.bind(this);
    this.toggleTooltip = this.toggleTooltip.bind(this);
    this.showTooltip = this.showTooltip.bind(this);
    this.handleTooltipBlur = this.handleTooltipBlur.bind(this);

    const showOnHover = this.#toggle.getAttribute('data-show-on-hover');

    if (showOnHover) {
      this.#toggle.addEventListener('mouseenter', this.showTooltip);
      this.#toggle.addEventListener('mouseleave', () => {
        if (this.#clickedOpen) return;
        this.hideTooltip();
      });
    }

    this.#toggle.addEventListener('click', () => {
      this.#clickedOpen = !this.#clickedOpen;
      this.toggleTooltip();
    });

    this.#toggle.addEventListener('blur', (event) => {
      const { relatedTarget } = event;

      if (relatedTarget === this.#tooltip) return;

      this.hideTooltip();
    });

    this.positionTooltip();
  }

  getIsExpanded() {
    return this.#toggle.getAttribute('aria-expanded') === 'true';
  }

  toggleTooltip() {
    const tooltipIsExpanded = this.getIsExpanded();
    if (tooltipIsExpanded && !this.#clickedOpen) {
      this.hideTooltip();
    } else if (!tooltipIsExpanded) {
      this.showTooltip();
    }
  }

  handleTooltipBlur() {
    this.hideTooltip();
  }

  showTooltip() {
    this.#toggle.setAttribute('aria-expanded', 'true');
    this.#tooltip.textContent = this.#tooltipMessage;
    this.#tooltip.classList.add('is-visible');
    this.positionTooltip();

    // Add tooltip blur listener to allow selection
    this.#tooltip.addEventListener('blur', this.handleTooltipBlur);
  }

  hideTooltip() {
    this.#toggle.setAttribute('aria-expanded', 'false');
    this.#tooltip.classList.remove('is-visible');
    this.#tooltip.textContent = '';
    this.#clickedOpen = false;

    // Remove tooltip blur listener
    this.#tooltip.removeEventListener('blur', this.handleTooltipBlur);
  }

  positionTooltip() {
    computePosition(this.#toggle, this.#tooltip,
      {
        placement: 'top-start',
        middleware: [offset({ crossAxis: 24}), flip(), shift()],
      }).then(({x, y}) => {
      Object.assign(this.#tooltip.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }
}

export default class ToggletipManager { // candidate for singleton
  static instance = null;
  #toggles = new Set();
  #toggletipInstances = [];
  #keydownListenerAttached = false;

  constructor() {
    if (ToggletipManager.instance) {
      throw new Error('Use ToggletipManager.getInstance() to access the manager.');
    }
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new ToggletipManager();
    }
    return this.instance;
  }

  init() {
    const toggles = document.querySelectorAll('[data-toggletip-control]');

    toggles.forEach(toggle => {
      this.createToggletip(toggle);
    });

    if (this.#keydownListenerAttached) return;

    document.addEventListener('keydown', this.handleKeydown.bind(this));
    this.#keydownListenerAttached = true;
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    this.#toggletipInstances.forEach(toggletip => {
      toggletip.hideTooltip();
    });
  }

  createToggletip(toggle) {
    if (this.#toggles.has(toggle)) return;

    this.#toggles.add(toggle);
    const toggletip = new Toggletip(toggle);
    this.#toggletipInstances.push(toggletip);
  }
};